/* Copyright (c) Trimble Inc. All rights reserved. */

import { PublicHttpClient, HttpClientConfig, HttpError } from '@tcp-base/toolkit';
import { useTokenStore } from '../stores/Token.store';
import { Logger } from '@tcp-base/toolkit';
import { TokenValidationHelper } from '../helpers/TokenValidator.helper';
import AuthRedrectionHelper from '../helpers/AuthRedirector.helper';

// ============================================================================
// Request Queue Types
// ============================================================================

/**
 * Represents a queued request waiting for token availability
 * Request Made → Token Available? → Token Expired?
                ├─ YES ─────────→ ├─ NO  → Execute immediately ✅
                │                  └─ YES → Queue request (expired) 🔄
                └─ NO ──────────────────→ Queue request (no token) 🔄
 */
interface QueuedRequest<TResponse> {
  /** Unique identifier for the queued request */
  id: string;
  /** API path to request */
  path: string;
  /** Request options (method, headers, body, etc.) */
  options: RequestInit & {
    params?: Record<string, string | number>;
    headers?: Record<string, string>;
  };
  /** Promise resolver to return response when request completes */
  resolve: (value: TResponse) => void;
  /** Promise rejector to handle errors */
  reject: (reason: Error) => void;
  /** Timestamp when request was queued */
  queuedAt: number;
}

/**
 * Configuration for SecureHttpClient.
 *
 * Extends the base HTTP client options with auth-specific behavior.
 */
export interface SecureHttpClientConfig extends HttpClientConfig {
  /**
   * When true (default), a 401 clears the local session and redirects to login.
   * Set to false for secondary APIs (e.g. validation-api) where a 401 should
   * surface to the caller as an error without ending the console session.
   */
  redirectOn401?: boolean;
}

/**
 * SecureHttpClient - Extends PublicHttpClient with automatic token management
 *
 * This client automatically:
 * - Subscribes to token updates from Token.store
 * - Injects Bearer token into all requests
 * - Updates headers when token changes
 * - Handles token expiration
 * - Queues requests when token is unavailable and processes them once token arrives
 *
 * @example
 * ```typescript
 * const apiClient = new SecureHttpClient({
 *   baseURL: 'https://api.example.com',
 *   queryClient: queryClient,
 * });
 *
 * // Token is automatically added to all requests
 * const data = await apiClient.request('/users');
 * // Request includes: Authorization: Bearer <token>
 * ```
 */
export class SecureHttpClient extends PublicHttpClient {
  private unsubscribe?: () => void;
  private currentToken?: string;
  /** Queue to hold requests when token is not available */
  private requestQueue: QueuedRequest<unknown>[] = [];
  /** Flag to prevent duplicate queue processing */
  private isProcessingQueue = false;
  /** Maximum time to wait for token before rejecting queued requests (default: 30 seconds) */
  private readonly queueTimeout: number;
  /** Guard to prevent multiple concurrent 401 re-auth flows */
  private is401Handling = false;
  /** Guard to prevent multiple concurrent no-token login redirects */
  private isRedirectingToLogin = false;
  /** Count of consecutive requests queued with no token — triggers login redirect at threshold */
  private noTokenQueueCount = 0;
  /** How many consecutive no-token requests before triggering a login redirect */
  private static readonly NO_TOKEN_REDIRECT_THRESHOLD = 10;
  /**
   * When true, 401 responses clear the session and redirect to login.
   * When false, 401s are propagated to the caller without ending the session.
   */
  private readonly redirectOn401: boolean;

  /**
   * Returns true when the OAuth callback is actively in progress (i.e. the
   * URL still has `?code=` OR TokenExchanger has signalled via sessionStorage
   * that a code exchange is underway).
   *
   * We must NOT call redirectToLogin() during this window, otherwise the
   * redirect clears the `code` parameter and restarts the flow, creating
   * an infinite loop.
   *
   * Note: `cleanOAuthCallbackUrl()` removes `?code=` from the URL BEFORE
   * the async exchange completes, so we cannot rely on the URL alone —
   * the sessionStorage flag is the reliable indicator.
   */
  private static isOAuthCallbackInProgress(): boolean {
    try {
      const hasCodeInUrl = new URLSearchParams(window.location.search).has('code');
      const hasExchangeFlag = sessionStorage.getItem('OAUTH_EXCHANGE_IN_PROGRESS') === 'true';
      return hasCodeInUrl || hasExchangeFlag;
    } catch {
      return false;
    }
  }

  constructor(config: SecureHttpClientConfig) {
    super(config);

    // Set queue timeout (default 30 seconds)
    this.queueTimeout = 30000;
    this.redirectOn401 = config.redirectOn401 ?? true;

    // Subscribe to token store changes
    this.subscribeToTokenUpdates();

    Logger.info(`SecureHttpClient initialized for ${config.baseURL}`);
  }

  /**
   * Subscribe to token store updates using Zustand
   * Automatically updates the authorization header when token changes
   * and processes any queued requests waiting for token
   */
  private subscribeToTokenUpdates(): void {
    // Zustand subscribe with selector
    this.unsubscribe = useTokenStore.subscribe((state) => {
      const accessToken = state.tokenDetails.access_token;
      if (accessToken && accessToken !== this.currentToken) {
        this.currentToken = accessToken;
        Logger.debug('SecureHttpClient: Token updated');

        // Token is now available — reset no-token counter and redirect guard
        this.noTokenQueueCount = 0;
        this.isRedirectingToLogin = false;

        // Process queued requests now that token is available
        this.processQueuedRequests();
      } else if (!accessToken && this.currentToken) {
        // Token was removed (logout)
        this.currentToken = undefined;
        Logger.debug('SecureHttpClient: Token removed');

        // Clear queue on logout
        this.clearQueueWithError(new Error('Token removed - user logged out'));
      }
    });

    // Set initial token if available
    const initialToken = useTokenStore.getState().tokenDetails.access_token;
    if (initialToken) {
      this.currentToken = initialToken;
      Logger.debug('SecureHttpClient: Initial token set');
    } else {
      // Zustand store starts empty — the token lives in IndexedDB and is only
      // populated after an async getTokenDetails() call.  Proactively trigger
      // that hydration here so that any requests queued before AuthChecker
      // finishes can be unblocked as soon as the token is available.
      // When getTokenDetails() resolves it writes to Zustand state, which fires
      // the subscription above → sets currentToken → drains the request queue.
      Logger.debug('SecureHttpClient: No token in store, hydrating from IndexedDB');
      useTokenStore
        .getState()
        .getTokenDetails()
        .then((tokenDetails) => {
          if (!tokenDetails?.access_token) {
            // IndexedDB is also empty. AuthChecker/TokenExchanger will handle
            // the redirect to login. SecureHttpClient's role here is only to
            // hydrate the token — not to redirect.
            Logger.debug(
              'SecureHttpClient: No token in IndexedDB after hydration — waiting for AuthChecker',
            );
          }
          // If a token was found, getTokenDetails() already wrote it to Zustand,
          // which fires the subscription above → sets currentToken → drains the queue.
        })
        .catch((error) => {
          Logger.warn('SecureHttpClient: Failed to hydrate token from IndexedDB:', error);
        });
    }
  }

  /**
   * Override request method to inject bearer token
   * If no token is available at all, the request is queued and executed once a token arrives.
   * An expired token is still sent: the server's 401 triggers re-authentication.
   */
  override async request<TResponse>(
    path: string,
    options: RequestInit & {
      params?: Record<string, string | number>;
      headers?: Record<string, string>;
    } = {},
  ): Promise<TResponse> {
    // Inject Authorization header if a token is available
    const authHeaders: Record<string, string> = {};

    // NOTE: we deliberately do NOT gate requests on the locally-computed token
    // expiry. An expired token is still sent and the server's 401 response is
    // the single trigger for re-authentication (see handle401()). Gating here
    // would park the request in the queue, so the server would never get the
    // chance to answer 401 and the user would never be redirected from the
    // action they actually performed (e.g. "View secret").
    const storedToken = this.currentToken ?? useTokenStore.getState().tokenDetails.access_token;

    if (storedToken) {
      if (TokenValidationHelper.isTokenExpired(storedToken)) {
        Logger.debug(
          'SecureHttpClient: Token is past its expiry, sending anyway and relying on server 401',
        );
      }
      this.currentToken = storedToken;
      authHeaders['Authorization'] = `Bearer ${storedToken}`;
    } else {
      // No token available - queue the request and wait for token update
      Logger.warn('SecureHttpClient: No token available, queuing request');

      if (!SecureHttpClient.isOAuthCallbackInProgress()) {
        // Only count and potentially redirect when we are NOT in the middle
        // of an OAuth callback — during the callback there is legitimately
        // no token yet and TokenExchanger will supply one shortly.
        this.noTokenQueueCount++;
        if (this.noTokenQueueCount >= SecureHttpClient.NO_TOKEN_REDIRECT_THRESHOLD) {
          Logger.warn(
            `SecureHttpClient: No token after ${this.noTokenQueueCount} requests, redirecting to login`,
          );
          this.redirectToLogin();
        }
      } else {
        Logger.debug(
          'SecureHttpClient: No token yet (OAuth callback in progress), queuing without counting',
        );
      }

      return this.queueRequest<TResponse>(path, options);
    }

    // Merge auth headers with provided headers
    const mergedOptions = {
      ...options,
      headers: {
        ...authHeaders,
        ...options.headers,
      },
    };

    // Call parent's request method with merged options and intercept 401
    try {
      return await super.request<TResponse>(path, mergedOptions);
    } catch (error) {
      if (error instanceof HttpError && error.status === 401) {
        // A 401 that means "signed in, but not recently enough" (the consumer secret
        // firewall, RFC 7807 `type: auth_time.exceeded`) must reach the caller so it can
        // start a fresh login and then show the secret. Every other 401 is a session
        // problem and goes through the generic handler, unless this client was configured
        // not to redirect.
        if (error.isAuthTimeExceeded) {
          Logger.warn(
            `SecureHttpClient: 401 auth_time.exceeded received for ${path} — skipping global handler`,
          );
        } else if (this.redirectOn401) {
          this.handle401();
        } else {
          Logger.warn(
            'SecureHttpClient: 401 Unauthorized — redirectOn401=false, propagating error to caller',
          );
        }
      }
      throw error;
    }
  }

  /**
   * Redirect to login when no valid token is available.
   *
   * Clears any queued requests and initiates re-authentication.
   * AuthRedrectionHelper.login() saves the current path so the user
   * is returned to the same page after a successful sign-in.
   *
   * A guard flag prevents multiple concurrent redirects.
   */
  private redirectToLogin(): void {
    if (this.isRedirectingToLogin) {
      Logger.debug('SecureHttpClient: login redirect already in progress, skipping duplicate');
      return;
    }
    this.isRedirectingToLogin = true;
    this.noTokenQueueCount = 0;

    // Reject queued requests so callers receive a clear error immediately
    this.clearQueueWithError(new Error('No token available — redirecting to login'));

    AuthRedrectionHelper.login().catch((error) => {
      Logger.error('SecureHttpClient: login redirect failed:', error);
      this.isRedirectingToLogin = false; // reset so a retry is possible if login itself fails
    });
  }

  /**
   * Handle a 401 Unauthorized response.
   *
   * Clears the local token (it is no longer accepted by the server), clears
   * the queued request list, and initiates re-authentication via
   * AuthRedrectionHelper.login().  The user will be redirected to the
   * Identity /authorize endpoint and returned to the same page after a
   * successful sign-in (handled by AuthChecker.handlePostLoginRedirect).
   *
   * A guard flag prevents this from firing more than once concurrently so
   * that parallel in-flight requests do not launch multiple login redirects.
   */
  private handle401(): void {
    if (this.is401Handling) {
      Logger.debug('SecureHttpClient: 401 already being handled, skipping duplicate');
      return;
    }
    this.is401Handling = true;
    Logger.warn('SecureHttpClient: 401 Unauthorized — clearing token and redirecting to login');

    // Clear the stale token
    this.currentToken = undefined;
    useTokenStore
      .getState()
      ?.removeTokenDetails()
      .catch((removeError) => {
        Logger.warn('SecureHttpClient: Failed to remove token details after 401:', removeError);
      });

    // Reject all queued requests immediately so callers get a clear error
    this.clearQueueWithError(new Error('Session expired — redirecting to login'));

    // Redirect to login; AuthRedrectionHelper.login() saves the current path
    // so the user is returned to the same page after authentication.
    AuthRedrectionHelper.login().catch((loginError) => {
      Logger.error('SecureHttpClient: login redirect after 401 failed:', loginError);
      this.is401Handling = false; // reset so a retry is possible if login itself fails
    });
  }

  /**
   * Get current token (for debugging/testing)
   */
  getCurrentToken(): string | undefined {
    return this.currentToken;
  }

  /**
   * Check if client has a valid token
   */
  hasToken(): boolean {
    return !!this.currentToken;
  }

  /**
   * Manually refresh token from store
   * Useful after login or token refresh
   */
  async refreshToken(): Promise<void> {
    try {
      const tokenDetails = await useTokenStore.getState().getTokenDetails();
      if (tokenDetails.access_token) {
        this.currentToken = tokenDetails.access_token;
        Logger.info('SecureHttpClient: Token manually refreshed');

        // Process any queued requests
        this.processQueuedRequests();
      } else {
        Logger.warn('SecureHttpClient: No token found during manual refresh');
      }
    } catch (error) {
      Logger.error('SecureHttpClient: Failed to refresh token', error);
      throw error;
    }
  }

  // ============================================================================
  // Request Queue Management
  // ============================================================================

  /**
   * Queue a request when token is not available
   * The request will be automatically executed once token becomes available
   *
   * @param path API endpoint path
   * @param options Request options
   * @returns Promise that resolves when request is processed
   */
  private queueRequest<TResponse>(
    path: string,
    options: RequestInit & {
      params?: Record<string, string | number>;
      headers?: Record<string, string>;
    },
  ): Promise<TResponse> {
    return new Promise<TResponse>((resolve, reject) => {
      const queuedRequest: QueuedRequest<TResponse> = {
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        path,
        options,
        resolve: resolve as (value: TResponse) => void,
        reject,
        queuedAt: Date.now(),
      };

      this.requestQueue.push(queuedRequest as QueuedRequest<unknown>);
      Logger.info(
        `SecureHttpClient: Request queued (${queuedRequest.id}). Queue size: ${this.requestQueue.length}`,
      );

      // Set timeout to reject request if token doesn't arrive in time
      setTimeout(() => {
        this.rejectTimedOutRequest(queuedRequest.id);
      }, this.queueTimeout);
    });
  }

  /**
   * Process all queued requests once token becomes available and is valid
   * Executes requests in FIFO order (first-in-first-out)
   */
  private async processQueuedRequests(): Promise<void> {
    // Prevent concurrent processing
    if (this.isProcessingQueue || this.requestQueue.length === 0) {
      return;
    }

    // No token available, can't process queue
    if (!this.currentToken) {
      Logger.warn('SecureHttpClient: Cannot process queue without token');
      return;
    }

    this.isProcessingQueue = true;
    Logger.info(`SecureHttpClient: Processing ${this.requestQueue.length} queued requests`);

    // Process all queued requests
    const queueCopy = [...this.requestQueue];
    this.requestQueue = [];

    for (const queuedRequest of queueCopy) {
      try {
        // Check if request has timed out
        const waitTime = Date.now() - queuedRequest.queuedAt;
        if (waitTime > this.queueTimeout) {
          Logger.warn(`SecureHttpClient: Skipping timed out request (${queuedRequest.id})`);
          queuedRequest.reject(
            new Error(`Request timed out after waiting ${waitTime}ms for token`),
          );
          continue;
        }

        // Add authorization header
        const authHeaders: Record<string, string> = {
          Authorization: `Bearer ${this.currentToken}`,
        };

        const mergedOptions: RequestInit & {
          params?: Record<string, string | number>;
          headers?: Record<string, string>;
        } = {
          ...queuedRequest.options,
          headers: {
            ...authHeaders,
            ...(queuedRequest.options.headers || {}),
          },
        };

        Logger.debug(`SecureHttpClient: Executing queued request (${queuedRequest.id})`);

        // Execute the request
        const response = await super.request(queuedRequest.path, mergedOptions);
        queuedRequest.resolve(response);

        Logger.debug(`SecureHttpClient: Queued request completed (${queuedRequest.id})`);
      } catch (error) {
        Logger.error(`SecureHttpClient: Queued request failed (${queuedRequest.id})`, error);
        queuedRequest.reject(error as Error);
      }
    }

    this.isProcessingQueue = false;
    Logger.info('SecureHttpClient: Queue processing completed');
  }

  /**
   * Reject a specific queued request that has timed out
   *
   * @param requestId Unique identifier of the request to reject
   */
  private rejectTimedOutRequest(requestId: string): void {
    const index = this.requestQueue.findIndex((req) => req.id === requestId);

    if (index !== -1) {
      const request = this.requestQueue[index];
      this.requestQueue.splice(index, 1);

      const waitTime = Date.now() - request.queuedAt;
      Logger.warn(`SecureHttpClient: Request timed out (${requestId}) after ${waitTime}ms`);

      request.reject(new Error(`Request timed out after ${waitTime}ms waiting for token`));
    }
  }

  /**
   * Clear all queued requests with an error
   * Used when token is removed (logout) or client is destroyed
   *
   * @param error Error to reject all queued requests with
   */
  private clearQueueWithError(error: Error): void {
    if (this.requestQueue.length === 0) {
      return;
    }

    Logger.warn(`SecureHttpClient: Clearing ${this.requestQueue.length} queued requests`);

    const queueCopy = [...this.requestQueue];
    this.requestQueue = [];

    queueCopy.forEach((request) => {
      request.reject(error);
    });
  }

  /**
   * Get current queue size (for debugging/monitoring)
   */
  getQueueSize(): number {
    return this.requestQueue.length;
  }

  /**
   * Cleanup: Unsubscribe from token store and clear request queue
   * Call this when the client is no longer needed
   */
  destroy(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
      Logger.debug('SecureHttpClient: Unsubscribed from token updates');
    }

    // Clear any pending queued requests
    this.clearQueueWithError(new Error('SecureHttpClient destroyed'));
  }
}
